import React, { useState } from "react";

const WORKER_URL = process.env.REACT_APP_WORKER_URL || "http://localhost:8787";

const fmt = (n) => {
  if (n == null || isNaN(n)) return "";
  return n < 0
    ? `-$${Math.abs(n).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`
    : `$${n.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
};
const pct = (n) => (n != null ? `${(n*100).toFixed(2)}%` : "");

const TIERS = [
  {min:0,max:2,pct:"5%"},{min:3,max:4,pct:"6%"},{min:5,max:5,pct:"7%"},
  {min:6,max:7,pct:"8%"},{min:8,max:9,pct:"9%"},{min:10,max:Infinity,pct:"10%"},
];

export default function App() {
  const [startDate,setStartDate] = useState("");
  const [endDate,setEndDate] = useState("");
  const [data,setData] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState(null);

  const generate = async () => {
    if (!startDate||!endDate) return;
    setLoading(true); setError(null); setData(null);
    try {
      const resp = await fetch(`${WORKER_URL}/api/commission`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({startDate,endDate}),
      });
      if (!resp.ok) { const e = await resp.json().catch(()=>({})); throw new Error(e.error||`API ${resp.status}`); }
      const result = await resp.json();
      if (result.error) throw new Error(result.error);
      setData(result);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!data) return;
    const ws = data.weekStarts;
    let csv = "Sales Rep,Type";
    ws.forEach(w => { csv += `,${w.label} Deals,${w.label} Units,${w.label} Tier,${w.label} RP Bonus,${w.label} Base Comm`; });
    csv += ",Gross Comm";
    ws.forEach(w => { csv += `,${w.label} Refund Units,${w.label} Refunds`; });
    csv += ",Total Comm";
    ws.forEach(w => { csv += `,${w.label} Collected`; });
    csv += "\n";
    data.reps.forEach(r => {
      csv += `"${r.rep}","${r.isFlat?'Flat '+pct(r.flatRate):'Standard'}"`;
      r.weeks.forEach(wk => { csv += `,${wk.deals},${wk.units},${pct(wk.tier)},${wk.rpBonus},${wk.baseCommission}`; });
      csv += `,${r.grossCommission}`;
      r.refundWeeks.forEach(rw => { csv += `,${rw.refundRRUnits||""},${rw.refundAmount||""}`; });
      csv += `,${r.totalComm}`;
      r.weeks.forEach(wk => { csv += `,${wk.collectedAmount}`; });
      csv += "\n";
    });
    // Concierge new sales row
    if (data.conciergeNewSalesByWeek?.some(v => v > 0)) {
      csv += `"Concierge New Sales",""`;
      ws.forEach(() => { csv += ",,,,,"; });
      csv += ",";
      ws.forEach(() => { csv += ",,"; });
      csv += ",";
      data.conciergeNewSalesByWeek.forEach(v => { csv += `,${v}`; });
      csv += "\n";
    }
    if (data.concierge?.length) {
      csv += "\nConcierge Rep,Renewals Collected,New Sales,Gross Payout,Refund Deduction,Payout\n";
      data.concierge.forEach(c => {
        csv += `"${c.rep}",${c.renewalsCollected},${c.newSalesCollected},${c.grossPayout},${c.refundDeduction},${c.payout}\n`;
      });
    }
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`commission-report-${startDate}-to-${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const weekCount = data?.weekStarts?.length || 0;

  return (
    <div style={s.root}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.logoRow}>
          <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="6" fill="#C8FF3E"/>
            <path d="M8 22V10l8 6-8 6z" fill="#0A0A0A"/>
            <path d="M16 22V10l8 6-8 6z" fill="#0A0A0A" opacity="0.5"/>
          </svg>
          <div>
            <div style={s.logoTitle}>SUPRA HUMAN</div>
            <div style={s.logoSub}>Commission Engine</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={s.controls}>
        <div style={s.fieldGroup}>
          <label style={s.label}>Start Date</label>
          <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={s.input}/>
        </div>
        <div style={s.fieldGroup}>
          <label style={s.label}>End Date</label>
          <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={s.input}/>
        </div>
        <button onClick={generate} disabled={loading||!startDate||!endDate}
          style={{...s.btn,opacity:loading||!startDate||!endDate?0.5:1,cursor:loading||!startDate||!endDate?"not-allowed":"pointer"}}>
          {loading?"Generating…":"Generate Report"}
        </button>
      </div>

      {/* Tiers */}
      <div style={s.tierBox}>
        <div style={s.tierTitle}>Commission Tiers</div>
        <div style={s.tierGrid}>
          {TIERS.map((t,i)=>(
            <div key={i} style={s.tierItem}>
              <span style={s.tierUnits}>{t.max===Infinity?`${t.min}+`:t.min===t.max?`${t.min}`:`${t.min}–${t.max}`} units</span>
              <span style={s.tierPct}>{t.pct}</span>
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div style={s.loadingWrap}>
          <div style={s.spinner}/><div style={{marginTop:16,color:"rgba(255,255,255,0.5)",fontSize:13}}>Pulling deals from HubSpot and calculating commissions…<br/>This typically takes 10–30 seconds.</div>
        </div>
      )}
      {error && <div style={s.error}>Error: {error}</div>}

      {/* ═══ SALES COMMISSION TABLE ═══ */}
      {data?.reps && (
        <div style={s.tableWrap}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Sales Commission Report</div>
            <div style={s.tableMeta}>{startDate} → {endDate} · {data.reps.length} reps · {weekCount} week{weekCount!==1?"s":""}</div>
          </div>
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{...s.th,...s.stickyCol,left:0,zIndex:3,textAlign:"left"}}>Sales Rep</th>
                  {/* Per-week: Deals, Units, Tier, RP Bonus, Base Comm */}
                  {data.weekStarts.map((w,i)=>(
                    <React.Fragment key={`wh-${i}`}>
                      <th style={s.th}>{w.label} Deals</th>
                      <th style={s.th}>{w.label} Units</th>
                      <th style={s.th}>{w.label} Tier</th>
                      <th style={s.th}>{w.label} RP Bonus</th>
                      <th style={s.th}>{w.label} Base Comm</th>
                    </React.Fragment>
                  ))}
                  <th style={{...s.th,background:"rgba(200,255,62,0.08)"}}>Gross Comm</th>
                  {/* Per-week refunds */}
                  {data.weekStarts.map((w,i)=>(
                    <React.Fragment key={`rh-${i}`}>
                      <th style={{...s.th,background:"rgba(239,68,68,0.06)"}}>{w.label} Refund Units</th>
                      <th style={{...s.th,background:"rgba(239,68,68,0.06)"}}>{w.label} Refunds</th>
                    </React.Fragment>
                  ))}
                  <th style={{...s.th,background:"rgba(200,255,62,0.12)"}}>Total Comm</th>
                  {/* Per-week Collected (at the end) */}
                  {data.weekStarts.map((w,i)=>(
                    <th key={`ch-${i}`} style={{...s.th,background:"rgba(255,255,255,0.04)"}}>{w.label} Collected</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.reps.map((rep,ri)=>(
                  <tr key={ri} style={ri%2!==0?{background:"rgba(255,255,255,0.02)"}:{}}>
                    <td style={{...s.td,...s.stickyCol,left:0,fontWeight:600,whiteSpace:"nowrap"}}>
                      {rep.rep}
                      {rep.isFlat && <span style={{marginLeft:8,fontSize:10,color:"rgba(200,255,62,0.6)",fontWeight:400}}>({pct(rep.flatRate)} flat)</span>}
                    </td>
                    {rep.weeks.map((wk,wi)=>(
                      <React.Fragment key={`wd-${wi}`}>
                        <td style={s.tdNum}>{wk.deals||""}</td>
                        <td style={s.tdNum}>{!rep.isFlat?(wk.units||""):""}</td>
                        <td style={s.tdNum}>{wk.deals>0?pct(wk.tier):""}</td>
                        <td style={s.tdNum}>{wk.rpBonus?fmt(wk.rpBonus):""}</td>
                        <td style={s.tdNum}>{wk.baseCommission?fmt(wk.baseCommission):""}</td>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.08)",fontWeight:700}}>{fmt(rep.grossCommission)}</td>
                    {rep.refundWeeks.map((rw,rwi)=>(
                      <React.Fragment key={`rd-${rwi}`}>
                        <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",color:rw.refundRRUnits?"#F87171":"inherit"}}>{rw.refundRRUnits||""}</td>
                        <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",color:rw.refundAmount?"#F87171":"inherit"}}>{rw.refundAmount?fmt(rw.refundAmount):""}</td>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)",fontWeight:700,color:rep.totalComm<0?"#F87171":"#C8FF3E"}}>{fmt(rep.totalComm)}</td>
                    {/* Collected at end */}
                    {rep.weeks.map((wk,wi)=>(
                      <td key={`wc-${wi}`} style={{...s.tdNum,background:"rgba(255,255,255,0.04)"}}>{wk.collectedAmount?fmt(wk.collectedAmount):""}</td>
                    ))}
                  </tr>
                ))}

                {/* TOTALS row */}
                <tr style={{borderTop:"2px solid rgba(200,255,62,0.3)"}}>
                  <td style={{...s.td,...s.stickyCol,left:0,fontWeight:700,color:"#C8FF3E"}}>TOTALS</td>
                  {data.weekStarts.map((_,wi)=>{
                    const tD=data.reps.reduce((a,r)=>a+(r.weeks[wi]?.deals||0),0);
                    const tU=data.reps.reduce((a,r)=>a+(r.weeks[wi]?.units||0),0);
                    const tR=data.reps.reduce((a,r)=>a+(r.weeks[wi]?.rpBonus||0),0);
                    const tB=data.reps.reduce((a,r)=>a+(r.weeks[wi]?.baseCommission||0),0);
                    return(<React.Fragment key={`tw-${wi}`}>
                      <td style={{...s.tdNum,fontWeight:700}}>{tD}</td>
                      <td style={{...s.tdNum,fontWeight:700}}>{tU}</td>
                      <td style={s.tdNum}/>
                      <td style={{...s.tdNum,fontWeight:700}}>{fmt(tR)}</td>
                      <td style={{...s.tdNum,fontWeight:700}}>{fmt(tB)}</td>
                    </React.Fragment>);
                  })}
                  <td style={{...s.tdNum,background:"rgba(200,255,62,0.08)",fontWeight:700,color:"#C8FF3E"}}>{fmt(data.reps.reduce((a,r)=>a+r.grossCommission,0))}</td>
                  {data.weekStarts.map((_,wi)=>{
                    const tRU=data.reps.reduce((a,r)=>a+(r.refundWeeks[wi]?.refundRRUnits||0),0);
                    const tRA=data.reps.reduce((a,r)=>a+(r.refundWeeks[wi]?.refundAmount||0),0);
                    return(<React.Fragment key={`tr-${wi}`}>
                      <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",fontWeight:700,color:tRU?"#F87171":"inherit"}}>{tRU||""}</td>
                      <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",fontWeight:700,color:tRA?"#F87171":"inherit"}}>{tRA?fmt(tRA):""}</td>
                    </React.Fragment>);
                  })}
                  <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)",fontWeight:700,color:"#C8FF3E"}}>{fmt(data.reps.reduce((a,r)=>a+r.totalComm,0))}</td>
                  {data.weekStarts.map((_,wi)=>{
                    const tC=data.reps.reduce((a,r)=>a+(r.weeks[wi]?.collectedAmount||0),0);
                    return <td key={`tc-${wi}`} style={{...s.tdNum,background:"rgba(255,255,255,0.04)",fontWeight:700}}>{fmt(tC)}</td>;
                  })}
                </tr>

                {/* Concierge New Sales row */}
                {data.conciergeNewSalesByWeek?.some(v => v > 0) && (
                  <tr>
                    <td style={{...s.td,...s.stickyCol,left:0,fontWeight:600,fontStyle:"italic",color:"rgba(255,255,255,0.5)"}}>Concierge New Sales</td>
                    {data.weekStarts.map((_,wi)=>(
                      <React.Fragment key={`cns-${wi}`}>
                        <td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.08)"}}/>
                    {data.weekStarts.map((_,wi)=>(
                      <React.Fragment key={`cnsr-${wi}`}>
                        <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)"}}/><td style={{...s.tdNum,background:"rgba(239,68,68,0.06)"}}/>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)"}}/>
                    {data.conciergeNewSalesByWeek.map((v,wi)=>(
                      <td key={`cnsv-${wi}`} style={{...s.tdNum,background:"rgba(255,255,255,0.04)",fontWeight:600,color:"rgba(255,255,255,0.5)"}}>{v?fmt(v):""}</td>
                    ))}
                  </tr>
                )}

                {/* Grand total with concierge new sales */}
                {data.conciergeNewSalesByWeek?.some(v => v > 0) && (
                  <tr style={{borderTop:"1px solid rgba(255,255,255,0.08)"}}>
                    <td style={{...s.td,...s.stickyCol,left:0,fontWeight:700,color:"#C8FF3E",fontSize:12}}>TOTAL + CONCIERGE</td>
                    {data.weekStarts.map((_,wi)=>(
                      <React.Fragment key={`gt-${wi}`}>
                        <td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/><td style={s.tdNum}/>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.08)"}}/>
                    {data.weekStarts.map((_,wi)=>(
                      <React.Fragment key={`gtr-${wi}`}>
                        <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)"}}/><td style={{...s.tdNum,background:"rgba(239,68,68,0.06)"}}/>
                      </React.Fragment>
                    ))}
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)"}}/>
                    {data.weekStarts.map((_,wi)=>{
                      const salesColl = data.reps.reduce((a,r)=>a+(r.weeks[wi]?.collectedAmount||0),0);
                      const conColl = data.conciergeNewSalesByWeek?.[wi] || 0;
                      return <td key={`gtv-${wi}`} style={{...s.tdNum,background:"rgba(255,255,255,0.04)",fontWeight:700,color:"#C8FF3E"}}>{fmt(salesColl + conColl)}</td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ CONCIERGE TABLE ═══ */}
      {data?.concierge?.length > 0 && (
        <div style={{...s.tableWrap,marginTop:40}}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Concierge Team Commission</div>
            <div style={s.tableMeta}>Flat 5% on collected amount</div>
          </div>
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{...s.th,textAlign:"left"}}>Concierge Rep</th>
                  <th style={s.th}>Renewals Collected</th>
                  <th style={s.th}>New Sales</th>
                  <th style={s.th}>Total Collected</th>
                  <th style={s.th}>Gross Payout (5%)</th>
                  <th style={{...s.th,background:"rgba(239,68,68,0.06)"}}>Refund Deduction</th>
                  <th style={{...s.th,background:"rgba(200,255,62,0.12)"}}>Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.concierge.map((c,i)=>(
                  <tr key={i} style={i%2!==0?{background:"rgba(255,255,255,0.02)"}:{}}>
                    <td style={{...s.td,fontWeight:600}}>{c.rep}</td>
                    <td style={s.tdNum}>{fmt(c.renewalsCollected)}</td>
                    <td style={s.tdNum}>{fmt(c.newSalesCollected)}</td>
                    <td style={s.tdNum}>{fmt(c.totalCollected)}</td>
                    <td style={s.tdNum}>{fmt(c.grossPayout)}</td>
                    <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",color:c.refundDeduction?"#F87171":"inherit"}}>{c.refundDeduction?fmt(c.refundDeduction):""}</td>
                    <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)",fontWeight:700,color:c.payout<0?"#F87171":"#C8FF3E"}}>{fmt(c.payout)}</td>
                  </tr>
                ))}
                <tr style={{borderTop:"2px solid rgba(200,255,62,0.3)"}}>
                  <td style={{...s.td,fontWeight:700,color:"#C8FF3E"}}>TOTALS</td>
                  <td style={{...s.tdNum,fontWeight:700}}>{fmt(data.concierge.reduce((a,c)=>a+c.renewalsCollected,0))}</td>
                  <td style={{...s.tdNum,fontWeight:700}}>{fmt(data.concierge.reduce((a,c)=>a+c.newSalesCollected,0))}</td>
                  <td style={{...s.tdNum,fontWeight:700}}>{fmt(data.concierge.reduce((a,c)=>a+c.totalCollected,0))}</td>
                  <td style={{...s.tdNum,fontWeight:700}}>{fmt(data.concierge.reduce((a,c)=>a+c.grossPayout,0))}</td>
                  <td style={{...s.tdNum,background:"rgba(239,68,68,0.06)",fontWeight:700,color:"#F87171"}}>{fmt(data.concierge.reduce((a,c)=>a+c.refundDeduction,0))}</td>
                  <td style={{...s.tdNum,background:"rgba(200,255,62,0.12)",fontWeight:700,color:"#C8FF3E"}}>{fmt(data.concierge.reduce((a,c)=>a+c.payout,0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ REFUND AUDIT TRAIL ═══ */}
      {data?.refundAudits?.length > 0 && (
        <div style={{...s.tableWrap,marginTop:40}}>
          <div style={s.tableHeader}>
            <div style={s.tableTitle}>Refund Breakdown</div>
            <div style={s.tableMeta}>How each refund deduction was calculated</div>
          </div>
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{...s.th,textAlign:"left"}}>Rep</th>
                  <th style={{...s.th,textAlign:"left"}}>Deal ID</th>
                  <th style={{...s.th,textAlign:"left"}}>Refund Week (Raw)</th>
                  <th style={{...s.th,textAlign:"left"}}>Week Start (Calc)</th>
                  <th style={{...s.th,textAlign:"left"}}>Approved Date</th>
                  <th style={s.th}>Source</th>
                  <th style={{...s.th,fontSize:9}}>Owner ID Used</th>
                  <th style={s.th}>Orig Week Deals</th>
                  <th style={s.th}>Orig Week Units</th>
                  <th style={s.th}>Tier Used</th>
                  <th style={s.th}>Is RP</th>
                  <th style={s.th}>Deduction</th>
                </tr>
              </thead>
              <tbody>
                {data.refundAudits.map((a,i)=>(
                  <tr key={i} style={i%2!==0?{background:"rgba(255,255,255,0.02)"}:{}}>
                    <td style={{...s.td,fontWeight:600}}>{a.repName}</td>
                    <td style={{...s.td,fontSize:11,fontFamily:"monospace"}}>{a.refundDealId}</td>
                    <td style={s.td}>{a.refundWeekUsedRaw}</td>
                    <td style={s.td}>{a.refundWeekStartCalc}</td>
                    <td style={s.td}>{a.refundApprovedDateRaw}</td>
                    <td style={{...s.tdNum,fontSize:11}}>{a.source}</td>
                    <td style={{...s.td,fontSize:9,fontFamily:"monospace",color:"rgba(255,255,255,0.35)"}}>{a.resolvedOwnerId||""}</td>
                    <td style={s.tdNum}>{a.origWeekDeals}</td>
                    <td style={s.tdNum}>{a.origWeekUnits}</td>
                    <td style={{...s.tdNum,fontWeight:700,color:"#C8FF3E"}}>{pct(a.tier)}</td>
                    <td style={s.tdNum}>{a.isRP?"Yes":""}</td>
                    <td style={{...s.tdNum,fontWeight:700,color:"#F87171"}}>{fmt(a.deduction)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export */}
      {data && (
        <div style={s.exportRow} data-no-print="true">
          <button onClick={exportCSV} style={s.exportBtn}>Export CSV</button>
          <button onClick={()=>window.print()} style={s.exportBtn}>Print / PDF</button>
        </div>
      )}

      {/* Footer */}
      <div style={s.footer} data-no-print="true">
        <div style={s.footerLine}/><div style={s.footerText}>BUILT BY RIPPED TECH</div>
      </div>
    </div>
  );
}

const s = {
  root:{minHeight:"100vh",background:"#0A0A0A",color:"#E5E5E5",fontFamily:"'DM Sans','Helvetica Neue',sans-serif",padding:"0 24px 48px",maxWidth:1800,margin:"0 auto"},
  header:{padding:"32px 0 24px",borderBottom:"1px solid rgba(255,255,255,0.06)",marginBottom:32},
  logoRow:{display:"flex",alignItems:"center",gap:14},
  logoTitle:{fontSize:18,fontWeight:800,letterSpacing:"0.12em",color:"#C8FF3E",lineHeight:1.2},
  logoSub:{fontSize:11,fontWeight:500,color:"rgba(255,255,255,0.4)",letterSpacing:"0.06em",textTransform:"uppercase",marginTop:2},
  controls:{display:"flex",gap:16,alignItems:"flex-end",flexWrap:"wrap",marginBottom:24},
  fieldGroup:{display:"flex",flexDirection:"column",gap:6},
  label:{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.08em"},
  input:{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,padding:"10px 14px",color:"#E5E5E5",fontSize:14,outline:"none",minWidth:180,height:42},
  btn:{background:"#C8FF3E",color:"#0A0A0A",border:"none",borderRadius:6,padding:"10px 28px",fontSize:14,fontWeight:700,letterSpacing:"0.02em",height:42,whiteSpace:"nowrap"},
  tierBox:{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8,padding:"14px 20px",marginBottom:32},
  tierTitle:{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10},
  tierGrid:{display:"flex",gap:20,flexWrap:"wrap"},
  tierItem:{display:"flex",gap:8,alignItems:"center"},
  tierUnits:{fontSize:12,color:"rgba(255,255,255,0.5)"},
  tierPct:{fontSize:13,fontWeight:700,color:"#C8FF3E"},
  loadingWrap:{textAlign:"center",padding:"64px 0"},
  spinner:{width:32,height:32,border:"3px solid rgba(255,255,255,0.1)",borderTopColor:"#C8FF3E",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto"},
  error:{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"12px 18px",color:"#F87171",fontSize:13,marginBottom:24},
  tableWrap:{marginBottom:32},
  tableHeader:{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12,flexWrap:"wrap",gap:8},
  tableTitle:{fontSize:16,fontWeight:700,color:"#E5E5E5"},
  tableMeta:{fontSize:12,color:"rgba(255,255,255,0.35)"},
  tableScroll:{overflowX:"auto",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8},
  table:{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:900},
  th:{padding:"10px 12px",textAlign:"right",fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.08)",whiteSpace:"nowrap",background:"rgba(255,255,255,0.02)"},
  td:{padding:"9px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:13,color:"#E5E5E5",background:"#0A0A0A"},
  tdNum:{padding:"9px 12px",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:13,color:"#E5E5E5",textAlign:"right",fontVariantNumeric:"tabular-nums"},
  stickyCol:{position:"sticky",background:"#0A0A0A",zIndex:2},
  exportRow:{display:"flex",gap:10,marginTop:14,justifyContent:"flex-end"},
  exportBtn:{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.6)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,padding:"8px 18px",fontSize:12,fontWeight:600,cursor:"pointer"},
  footer:{textAlign:"center",marginTop:64,paddingBottom:24},
  footerLine:{width:40,height:1,background:"rgba(255,255,255,0.1)",margin:"0 auto 12px"},
  footerText:{fontSize:9,color:"rgba(255,255,255,0.2)",letterSpacing:"0.2em",fontWeight:700},
};
